import { AdvancedSettingsPage } from './advanced-settings/AdvancedSettingsPage'
import { HikvisionSettingsPage } from './hikvision-settings/HikvisionSettingsPage'
import { MobileAppPage } from './mobile-app/MobileAppPage'
import { NotificationsPage } from './notifications/NotificationsPage'
import { OnlinePaymentsPage } from './online-payments/OnlinePaymentsPage'
import { OpeningHoursPage } from './opening-hours/OpeningHoursPage'
import { SalesChannelsPage } from './sales-channels/SalesChannelsPage'
import { SurchargesPage } from './surcharges/SurchargesPage'
import { VatCategoriesPage } from './vat-categories/VatCategoriesPage'
import { SettingsAiTranslatePage } from './settings-ai-translate/SettingsAiTranslatePage'
import { SettingsBackupsPage } from './settings-backups/SettingsBackupsPage'
import { SettingsBillingPage } from './settings-billing/SettingsBillingPage'
import { SettingsEmailPage } from './settings-email/SettingsEmailPage'
import { SettingsFranchisePage } from './settings-franchise/SettingsFranchisePage'
import { SettingsGeneralPage } from './settings-general/SettingsGeneralPage'
import { SettingsPushupPage } from './settings-pushup/SettingsPushupPage'
import { SettingsIntegrationsPage } from './settings-integrations/SettingsIntegrationsPage'
import { SettingsMonitoringPage } from './settings-monitoring/SettingsMonitoringPage'
import { SettingsNotificationsPage } from './settings-notifications/SettingsNotificationsPage'
import { SettingsSmsPage } from './settings-sms/SettingsSmsPage'
import { SalesHubPage, SettingsHubPage, TranslationsHubPage } from './settings-hub/SettingsHubPage'
import { SettingsTranslationsPage } from './settings-translations/SettingsTranslationsPage'

/**
 * `manager-settings` — everything under *Systémové nastavenia*.
 *
 * Ported: settings-general, opening-hours, settings-billing, settings-franchise, settings-email,
 * settings-sms, settings-notifications, notifications, settings-integrations, advanced-settings,
 * settings-backups, settings-monitoring, hikvision-settings, online-payments, settings-translations,
 * settings-ai-translate, mobile-app.
 *
 * **All seventeen are ported.** `mobile-app` stays until the owner signs off on §9.6's retirement of the
 * driver app; the page says so on itself.
 *
 * **One rule across the module, and it is FB-001's answer.** Every settings controller reads with
 * `@RequireAuth({"main_admin", "admin"})` and writes with `@RequireAuth("main_admin")`. So a route
 * admits whoever the server lets read — which is the sidebar's gate — and each page disables what the
 * server will not let them store, saying which. `write-access.ts` holds it, together with the four
 * sections an `admin` may write as well (`hikvision`, `backups`, `apk`, `notifications`).
 *
 * F0 framed this as a choice between letting an admin in and hiding the item. The server's answer is
 * neither: let them in, and be honest about the save.
 */
export const managerSettingsModule = {
  name: 'manager-settings',
  routes: [
    {
      /**
       * §9.208 — *Nastavenia*, five screens behind one sidebar entry, picked from inside.
       *
       * `anyPermission`, because these are not one gate: three of the five flags are held by the
       * waiter in the restored data and `settings_monitoring` is not. Gated on all five it would
       * hide from somebody who may see three; gated on none it would offer a tab the router
       * refuses. The five routes it folds up all still answer — a hub must not be a wall in front
       * of a bookmark.
       */
      path: '/settings',
      page: 'settings-hub',
      guard: {
        systemRoles: ['main_admin', 'admin'],
        anyPermission: [
          'settings_general',
          'settings_sms',
          'settings_email',
          'settings_notifications',
          'settings_monitoring',
        ],
      },
      component: SettingsHubPage,
    },
    {
      /** §9.208 — *Preklady*: the settings and the editor, which legacy filed as two items. */
      path: '/translations',
      page: 'translations-hub',
      guard: { permission: 'translations_admin' },
      component: TranslationsHubPage,
    },
    {
      /**
       * §9.308, owner item A6 — *Predaj a účtovanie*, three sidebar items become one entry.
       *
       * **The guard is the three routes' own, unchanged**, because all three carry exactly
       * `{main_admin, admin}`. No `anyPermission` here and that is not an omission: there is no
       * permission flag for any of the three in either catalogue, so gating on one would lock out
       * the admin the server admits — FB-001's shape, and the same reasoning `/settings-billing`
       * carries.
       *
       * The three routes it folds up all still answer.
       */
      path: '/sales-settings',
      page: 'sales-hub',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: SalesHubPage,
    },
    {
      // *Všeobecné nastavenia* in the sidebar, gated on `settings_general`. Legacy's router also
      // required `main_admin`, which is FB-001 — the server lets an admin read, so the route does too
      // and the page refuses the save.
      path: '/settings-general',
      page: 'settings-general',
      // The roles are the server's read set and they are not redundant with the flag: **`waiter` holds
      // `settings_general`** in the restored data, and the GET is `{main_admin, admin}`. Without the role
      // gate the route would admit somebody the server refuses to answer — FB-001 inverted.
      guard: {
        systemRoles: ['main_admin', 'admin'],
        permission: 'settings_general',
        franchiseModule: 'settings_general',
      },
      component: SettingsGeneralPage,
    },
    {
      // *Otváracie hodiny* in the sidebar. The write is gated on `opening_hours_edit` **or**
      // `main_admin`, so the page asks the permission and not the role — a waiter holding the flag may
      // edit, and an admin without it may not.
      path: '/opening-hours',
      page: 'opening-hours',
      guard: { permission: 'opening_hours' },
      component: OpeningHoursPage,
    },
    {
      // §9.298 - its own route so the hub tab and a deep link agree, like every other tab.
      path: '/settings-pushup',
      page: 'settings-pushup',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_notifications' },
      component: SettingsPushupPage,
    },
    {
      // Not in the sidebar — legacy reaches it by direct link only, and its router required
      // `main_admin`. There is **no `settings_billing` permission**: the flag appears in neither
      // catalogue and no role row grants it, so gating on one would lock out the admin the server admits.
      // The guard is therefore the GET's own `@RequireAuth({"main_admin", "admin"})`.
      path: '/settings-billing',
      page: 'settings-billing',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: SettingsBillingPage,
    },
    {
      // Same shape, same reason — there is no `settings_franchise` permission either.
      path: '/settings-franchise',
      page: 'settings-franchise',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: SettingsFranchisePage,
    },
    {
      // *E-mail nastavenia* in the sidebar, gated on `settings_email` — a flag the **waiter** role holds
      // in the restored data, against a `PUT /loyalty/settings` of `main_admin` and a GET of
      // `{main_admin, admin}`. So the roles come first and the flag stays alongside it, as
      // `/settings-general` does.
      path: '/settings-email',
      page: 'settings-email',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_email' },
      component: SettingsEmailPage,
    },
    {
      // *SMS nastavenia* in the sidebar. `settings_sms` is another flag the waiter holds, and every
      // endpoint behind this page is `main_admin` — except `POST /sms/send-manual`, which an `admin` may
      // call. The page still refuses the send for an admin, because the settings it depends on are not
      // theirs to fix if the token is wrong.
      path: '/settings-sms',
      page: 'settings-sms',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_sms' },
      component: SettingsSmsPage,
    },
    {
      // *Notifikácie & kanály* in the sidebar, gated on `settings_notifications` — held by the waiter too.
      // Every rule-changing endpoint on the controller is `main_admin`; the GET admits an admin.
      path: '/settings-notifications',
      page: 'settings-notifications',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_notifications' },
      component: SettingsNotificationsPage,
    },
    {
      // Not in the sidebar — legacy reaches the staff inbox by direct link. A different page from
      // `/settings-notifications`, which is about how *customers* are told things; this is what the system
      // noticed. `GET /notifications` and the two mark-read posts admit an admin, so the guard is the roles
      // and nothing narrower.
      path: '/notifications',
      page: 'notifications',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: NotificationsPage,
    },
    {
      // *Prepojenie Wolt / Bolt* in the sidebar. The two platform sections are gated per franchise module
      // (`settings_integrations.wolt`, `…bolt`) inside the page, as legacy does — tier gating fails open when
      // the tier is unknown, which it is on the restored data.
      path: '/settings-integrations',
      page: 'settings-integrations',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_integrations', franchiseModule: 'settings_integrations' },
      component: SettingsIntegrationsPage,
    },
    {
      // Not in the sidebar (FB-004). No permission exists for it and legacy's router required `main_admin`;
      // the page reads `/settings/general`, which admits an admin, and writes nothing at all — so the guard
      // is the read set.
      path: '/advanced',
      page: 'advanced-settings',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: AdvancedSettingsPage,
    },
    {
      // *Zálohy* in the sidebar. One of the four sections an `admin` may write as well as read — every
      // `/backups/*` endpoint is `@RequireAuth({"main_admin", "admin", "manager"})`, and `manager` is a role
      // no employee holds (recorded in `MIGRATION-STATUS.md`).
      path: '/settings-backups',
      page: 'settings-backups',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_backups' },
      component: SettingsBackupsPage,
    },
    {
      // *Monitoring* in the sidebar. Reads and writes `sms_settings` — `main_admin` on both, like
      // `/settings-sms` — and `POST /health-monitor/test` is `main_admin` too.
      path: '/settings-monitoring',
      page: 'settings-monitoring',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_monitoring' },
      component: SettingsMonitoringPage,
    },
    {
      // *Hikvision* in the sidebar. One of the four sections an `admin` may write as well as read: every
      // `/hikvision/*` endpoint is `@RequireAuth({"main_admin", "admin"})`.
      path: '/hikvision',
      page: 'hikvision-settings',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'hikvision', franchiseModule: 'hikvision' },
      component: HikvisionSettingsPage,
    },
    {
      // *Online platby* in the sidebar. The archive and the refund are gated on **permissions** —
      // `@RequirePermission({"online_payments", "online_payments_view"})` and `…_refund` - so a waiter
      // holding `online_payments_view` may read it and cannot refund (`V51`, BACKEND.md §10A.134);
      // only `PUT /settings/payments` is `main_admin`, and the page
      // disables just those two toggles.
      path: '/online-payments',
      page: 'online-payments',
      guard: { anyPermission: ['online_payments', 'online_payments_view'], franchiseModule: 'online_payments' },
      component: OnlinePaymentsPage,
    },
    {
      // *Preklady* in the sidebar, gated on `translations_admin`. Unusually for this module, **an admin may
      // write**: the `PATCH`, the `DELETE` and the bulk delete are all `{main_admin, admin}` — the memory is
      // not a settings blob and does not follow the read/write split. Recorded in `write-access.ts`.
      path: '/settings-translations',
      page: 'settings-translations',
      guard: { permission: 'translations_admin' },
      component: SettingsTranslationsPage,
    },
    {
      // *AI preklad* in the sidebar, gated on `settings_ai_translate` in legacy's nav — a flag in no
      // catalogue and granted by no role, so the guard is `translations_admin`, which the same person holds
      // and which the translation surface already uses. The write is `PUT /settings/basic`, `main_admin`.
      path: '/settings-ai-translate',
      page: 'settings-ai-translate',
      guard: { permission: 'translations_admin' },
      component: SettingsAiTranslatePage,
    },
    {
      // *Mobilná aplikácia* in the sidebar, gated on `mobile_app`. One of the four sections an `admin` may
      // write as well as read: every `/apk/*` endpoint is `@RequireAuth({"main_admin", "admin"})`.
      // §9.6 retires this surface once the in-manager driving screen ships; the removal needs the owner's
      // sign-off, so the page is ported and states that itself.
      path: '/mobile-app',
      page: 'mobile-app',
      guard: { systemRoles: ['main_admin', 'admin'], permission: 'mobile_app' },
      component: MobileAppPage,
    },
    {
      // §9.5's channel administration — a **justified EXTRA**: legacy has no such screen, and
      // `route-diff.test.ts` requires every extra to cite the upgrade it implements.
      //
      // The guard is the module's own settings rule rather than a legacy nav flag, because there is no
      // legacy nav flag: `GET /sales-channels` admits every role (each order screen needs to know what its
      // channel does) and the three writes are `{main_admin, admin}`.
      path: '/sales-channels',
      page: 'sales-channels',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: SalesChannelsPage,
    },
    {
      // §9.5's DPH administration — a **justified EXTRA** for the same reason as `/sales-channels`.
      // `VatController`'s three writes are `{main_admin, admin}`.
      path: '/surcharges',
      page: 'surcharges',
      // §9.5's deposits, a **justified EXTRA** for the same reason as the two above: five endpoints and
      // no way in — `surcharge_definitions` holds 0 rows. The three writes here are `main_admin` only,
      // stricter than the attachment endpoint on a product (`{main_admin, admin}`), so `surcharges` is
      // deliberately absent from `ADMIN_WRITABLE`.
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: SurchargesPage,
    },
    {
      path: '/vat-categories',
      page: 'vat-categories',
      guard: { systemRoles: ['main_admin', 'admin'] },
      component: VatCategoriesPage,
    },
  ],
} as const

export {
  AdvancedSettingsPage,
  HikvisionSettingsPage,
  MobileAppPage,
  NotificationsPage,
  OnlinePaymentsPage,
  OpeningHoursPage,
  SalesChannelsPage,
  VatCategoriesPage,
  SettingsAiTranslatePage,
  SettingsBackupsPage,
  SettingsBillingPage,
  SettingsEmailPage,
  SettingsFranchisePage,
  SettingsGeneralPage,
  SettingsIntegrationsPage,
  SettingsMonitoringPage,
  SettingsNotificationsPage,
  SettingsSmsPage,
  SettingsTranslationsPage,
}

// §9.304 - the app's reachability guard reads these to know a route is reachable as a hub tab rather
// than as a sidebar entry. Exported for that, and for the same reason `DELIVERY_TABS` is.
export { SALES_TABS, SETTINGS_TABS, TRANSLATION_TABS } from './settings-hub/settings-hub'
